/**
 * TEE Attestation Display Component
 * Shows Trusted Execution Environment proof in a user-friendly, verifiable format
 */

import { Shield, CheckCircle, Copy, ExternalLink } from 'lucide-react';
import { useState } from 'react';

interface TEEAttestationProps {
  proof: {
    output_hash?: string;
    attestation?: {
      available: boolean;
      image_digest?: string;
      platform?: string;
      instance_id?: string;
      timestamp?: string;
      token?: string;
    };
    binding_timestamp?: string;
  };
  proofId?: string;
}

export function TEEAttestation({ proof, proofId }: TEEAttestationProps) {
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const copyToClipboard = async (text: string, field: string) => {
    await navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const attestation = proof.attestation;

  return (
    <div className="bg-white rounded-lg shadow-sm p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Shield className="w-6 h-6 text-green-600" />
          <h3 className="text-lg font-semibold text-gray-900">TEE Attestation</h3>
        </div>
        {attestation?.available && (
          <span className="flex items-center gap-1 text-sm text-green-600 font-medium">
            <CheckCircle className="w-4 h-4" />
            Verified
          </span>
        )}
      </div>

      {/* Key Information Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Platform */}
        {attestation?.platform && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="text-sm font-medium text-blue-900 mb-1">Platform</div>
            <div className="text-sm text-blue-700 font-mono">{attestation.platform}</div>
            <div className="text-xs text-blue-600 mt-1">
              {attestation.platform === 'GCE_AMD_SEV' && 'Google Cloud AMD SEV-SNP'}
            </div>
          </div>
        )}

        {/* Timestamp */}
        {attestation?.timestamp && (
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
            <div className="text-sm font-medium text-purple-900 mb-1">Attestation Time</div>
            <div className="text-sm text-purple-700">
              {new Date(attestation.timestamp).toLocaleString()}
            </div>
            <div className="text-xs text-purple-600 mt-1 font-mono">
              {attestation.timestamp}
            </div>
          </div>
        )}
      </div>

      {/* Output Hash */}
      {proof.output_hash && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm font-medium text-gray-900">Output Hash</div>
            <button
              onClick={() => copyToClipboard(proof.output_hash!, 'output_hash')}
              className="text-gray-600 hover:text-gray-900 transition-colors"
              title="Copy hash"
            >
              {copiedField === 'output_hash' ? (
                <CheckCircle className="w-4 h-4 text-green-600" />
              ) : (
                <Copy className="w-4 h-4" />
              )}
            </button>
          </div>
          <div className="text-xs text-gray-700 font-mono break-all">
            {proof.output_hash}
          </div>
          <div className="text-xs text-gray-500 mt-1">
            SHA-256 hash of the complete query result
          </div>
        </div>
      )}

      {/* Proof ID */}
      {proofId && (
        <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm font-medium text-indigo-900">Proof Chain ID</div>
            <button
              onClick={() => copyToClipboard(proofId, 'proof_id')}
              className="text-indigo-600 hover:text-indigo-900 transition-colors"
              title="Copy Proof ID"
            >
              {copiedField === 'proof_id' ? (
                <CheckCircle className="w-4 h-4 text-green-600" />
              ) : (
                <Copy className="w-4 h-4" />
              )}
            </button>
          </div>
          <div className="text-xs text-indigo-700 font-mono">
            {proofId}
          </div>
          <div className="text-xs text-indigo-600 mt-1">
            Persistent identifier for this proof in KnowledgeFlowDB
          </div>
        </div>
      )}

      {/* Attestation Token (Collapsible) */}
      {attestation?.token && (
        <details className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <summary className="cursor-pointer text-sm font-medium text-gray-900 hover:text-gray-700">
            JWT Attestation Token (Click to expand)
          </summary>
          <div className="mt-3 space-y-2">
            <div className="flex items-center justify-between">
              <div className="text-xs text-gray-600">Full JWT token for independent verification</div>
              <button
                onClick={() => copyToClipboard(attestation.token!, 'token')}
                className="text-gray-600 hover:text-gray-900 transition-colors"
                title="Copy token"
              >
                {copiedField === 'token' ? (
                  <CheckCircle className="w-4 h-4 text-green-600" />
                ) : (
                  <Copy className="w-4 h-4" />
                )}
              </button>
            </div>
            <div className="text-xs text-gray-700 font-mono break-all bg-white p-3 rounded border border-gray-300 max-h-40 overflow-y-auto">
              {attestation.token}
            </div>
            <a
              href="https://jwt.io"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800"
            >
              <ExternalLink className="w-3 h-3" />
              Verify on jwt.io
            </a>
          </div>
        </details>
      )}

      {/* Verification Instructions */}
      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
        <div className="text-sm font-medium text-green-900 mb-2">
          How to Verify This Proof
        </div>
        <ol className="text-xs text-green-700 space-y-1 list-decimal list-inside">
          <li>Copy the JWT attestation token above</li>
          <li>Visit <a href="https://jwt.io" target="_blank" rel="noopener noreferrer" className="underline">jwt.io</a> to decode and verify the signature</li>
          <li>Verify the output_hash matches your expected result</li>
          <li>Check the timestamp is within acceptable timeframe</li>
          <li>Confirm platform is {attestation?.platform || 'TEE-enabled'}</li>
        </ol>
      </div>

      {/* Raw JSON (Collapsible) */}
      <details className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <summary className="cursor-pointer text-sm font-medium text-gray-900 hover:text-gray-700">
          Raw Proof Data (Advanced)
        </summary>
        <div className="mt-3 bg-white rounded border border-gray-300 p-3 font-mono text-xs overflow-x-auto max-h-60 overflow-y-auto">
          <pre>{JSON.stringify(proof, null, 2)}</pre>
        </div>
      </details>
    </div>
  );
}
